<template>
  <transition name="fade-slide">
    <b-card class="subscription-card" no-body>
      <b-card-header>
        <b-card-title>Mensajería</b-card-title>
      </b-card-header>
      <b-card-body class="pb-0">
        <b-row align-v="center" class="mb-2">
          <b-col cols="12">
            <div class="avatar-badge-wrapper">
              <div
                class="subscription-avatar avatar-as-div"
                role="img"
                aria-label="plan-avatar"
              >
                <feather-icon
                  size="24"
                  icon="MessageSquareIcon"
                  class="text-white"
                />
              </div>
              <div
                class="subscription-badge badge"
                :class="`badge-${statusVariant}`"
              >
                {{ statusLabel }}
              </div>
            </div>
          </b-col>
        </b-row>

        <b-card-title class="plan-title">
          {{ planName }}
        </b-card-title>

        <b-row>
          <b-col cols="12" md="6">
            <div class="messages-section mb-3">
              <div class="messages-text">
                <span class="text-danger">{{ messagesConsumed }}</span> /
                <span class="text-success">{{ messagesPaid }}</span> mensajes
                consumidos
              </div>

              <div class="progress-bar-wrapper">
                <div
                  class="progress-bar-fill"
                  :style="{ width: progressPercentage + '%' }"
                ></div>
              </div>

              <b-badge pill variant="light-success" class="mt-2 p-1">
                {{ messagesRemaining }} mensajes restantes
              </b-badge>
            </div>
          </b-col>

          <b-col cols="12" md="6">
            <b-card class="info-box" no-body>
              <b-card-body>
                <b-row class="mb-2">
                  <b-col cols="6" class="info-label">
                    <feather-icon size="24" class="mr-1" icon="CalendarIcon" />
                    <span>Vigente hasta</span>
                  </b-col>
                  <b-col cols="6" class="text-right info-value">
                    {{ vigenteHastaFormatted }}
                  </b-col>
                </b-row>

                <b-row class="mb-2">
                  <b-col cols="6" class="info-label">
                    <feather-icon
                      size="24"
                      class="mr-1 text-success"
                      icon="CheckCircleIcon"
                    />
                    <span>{{ typePayment }}</span>
                  </b-col>
                  <b-col cols="6" class="text-right info-value">
                    <div class="price">
                      {{ formatCurrency(price) }}
                    </div>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
  </transition>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BCardTitle,
  BButton,
  BCardText,
  BBadge,
  BCardHeader,
} from "bootstrap-vue";
import { type } from "vue-json-editor/assets/jsoneditor";

export default {
  name: "SubscriptionCard",
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BCardTitle,
    BCardText,
    BButton,
    BBadge,
    BCardHeader,
  },
  props: {
    planName: { type: String, required: true },
    startDate: { type: String, required: false, default: null },
    expiresAt: { required: false, default: null },
    price: { type: [String, Number], default: "0.00" },
    messagesConsumed: { type: Number, default: 0 },
    messagesPaid: { type: Number, default: 0 },
    messagesRemaining: { type: Number, default: 0 },
    typePayment: { type: String, default: "" },
  },
  computed: {
    progressPercentage() {
      if (!this.messagesPaid) return 0;
      return Math.min((this.messagesConsumed / this.messagesPaid) * 100, 100);
    },
    vigenteHastaFormatted() {
      if (!this.expiresAt) return "Fecha no disponible";
      return new Date(this.expiresAt).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    amountFormatted() {
      return `$${this.price}`;
    },
    subscriptionStatus() {
      // Determine subscription status based on expiresAt
      if (!this.expiresAt) return "expired";
      const end = new Date(this.expiresAt);
      if (isNaN(end.getTime())) return "expired";
      const now = new Date();
      return now > end ? "expired" : "active";
    },
    statusLabel() {
      return {
        active: "PLAN ACTIVO",
        expired: "PLAN VENCIDO",
      }[this.subscriptionStatus];
    },
    statusVariant() {
      return {
        active: "primary",
        expired: "danger",
      }[this.subscriptionStatus];
    },
    ctaVariant() {
      return this.subscriptionStatus === "expired" ? "danger" : "primary";
    },
  },
  methods: {
    formatCurrency(price) {
      const value = Number(price) || 0;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    },
  },
};
</script>

<style scoped>
/* Card */
.subscription-card {
  border-radius: 20px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.06);
}

/* Texts */
.plan-title {
  font-size: 22px;
  font-weight: 700;
}

.plan-description {
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 16px;
}

/* Info box */
.info-box {
  border-radius: 16px;
  background: #f8f9fa;
}

.date {
  font-size: 13px;
  color: #6c757d;
}

.date-value {
  font-weight: 600;
  margin-bottom: 8px;
}

.price {
  font-size: 20px;
  font-weight: 700;
}

.price small {
  font-size: 12px;
  color: #6c757d;
}

/* CTA animation */
.cta-btn {
  border-radius: 14px;
  padding: 8px 14px;
  font-weight: 600;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.cta-btn:active {
  transform: scale(0.96);
}

/* Avatar & badge stacking */
.avatar-badge-wrapper {
  position: relative;
  display: inline-block;
  min-width: 180px;
  height: 48px;
}
.subscription-avatar {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
}
.avatar-as-div {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #efb810;
}
.subscription-badge {
  position: absolute;
  left: 25px;
  top: 16px;
  transform: translateY(-50%);
  z-index: 1;
  min-width: 110px;
  padding: 10px 50px;
  border-radius: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Variant backgrounds (basic) */
.subscription-badge.badge-success {
  background-color: #52d78ecd;
  color: #fff;
}
.subscription-badge.badge-primary {
  background-color: #cfe2ff;
  color: #084298;
}
.subscription-badge.badge-danger {
  background-color: #ef6e78;
  color: #842029;
}

.subscription-card {
  border-radius: 22px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.06);
}

/* Company */
.company-name {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 14px;
}

/* Messages */
.messages-text {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
}

.progress-bar-wrapper {
  width: 100%;
  height: 10px;
  background: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #6b5cf6, #8f7df7);
  border-radius: 999px;
  transition: width 0.6s ease;
}

/* Info box */
.info-box {
  background: #f8f9fa;
  border-radius: 18px;
}

.info-label {
  font-size: 13px;
  color: #6c757d;
}

.info-value {
  font-size: 16px;
  font-weight: 600;
}

/* CTA */
/* .cta-btn {
  border-radius: 14px;
  font-weight: 600;
  padding: 8px 14px;
  transition: transform 0.15s ease;
}

.cta-btn:active {
  transform: scale(0.96);
} */

/* Animations */
.fade-slide-enter-active {
  transition: all 0.45s ease;
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(20px);
}
</style>
